import React from 'react';
import { useTranslation } from 'react-i18next';

import { PaneCard, Divider } from '../../../../components/wizard';
import { formatAmountWithCode, type Billing } from '../../../../data/billing';
import { legalUrl } from '../../../../data/legal';
import { VoucherField, VoucherChip } from '../VoucherField';
import { LifetimeBadge } from '../LifetimeBadge';
import {
  Root,
  SummaryLabel,
  Row,
  RowLabels,
  RowLabel,
  RowSublabel,
  CodePill,
  RowPrice,
  RowAmount,
  RowNote,
  Terms,
  TermsLabel,
  FooterTotal,
  FooterLabels,
  FooterLabel,
  FooterSub,
  FooterAmount,
} from './Billing.styles';

const EMDASH = '–';

export type BillingRowProps = {
  label: string;
  sublabel?: string;
  /** Render the sublabel in the success/green tone (vs. muted grey). */
  sublabelSuccess?: boolean;
  symbol?: string;
  /** ISO currency code (e.g. "USD"/"MXN") shown trailing the amount to disambiguate the $ glyph. */
  currencyCode: string;
  amount?: number | null;
  note?: string;
  /** Render the amount as a discount: a leading `−` and the success/green tone (for the voucher). */
  discount?: boolean;
  /** Render the sublabel as a monospaced code pill (the voucher code) instead of plain text. */
  sublabelPill?: boolean;
  /** A custom sublabel node (e.g. the removable voucher chip), rendered in place of `sublabel`. */
  sublabelNode?: React.ReactNode;
};

/** A billing line: bold label (+ optional sublabel) and a price (+ note). */
export const BillingRow = ({
  label,
  sublabel,
  sublabelSuccess,
  symbol,
  currencyCode,
  amount,
  note,
  discount,
  sublabelPill,
  sublabelNode,
}: BillingRowProps): React.ReactElement => (
  <Row>
    <RowLabels>
      <RowLabel text={label} tag="span" />
      {sublabelNode ? (
        sublabelNode
      ) : sublabel ? (
        sublabelPill ? (
          <CodePill text={sublabel} tag="span" />
        ) : (
          <RowSublabel text={sublabel} tag="span" $success={sublabelSuccess} />
        )
      ) : null}
    </RowLabels>
    {symbol != null ? (
      <RowPrice>
        <RowAmount
          text={discount ? '−{{symbol}}{{amount}}' : '{{symbol}}{{amount}}'}
          tag="span"
          $success={discount}
          placeholders={{ symbol, amount: amount == null ? EMDASH : formatAmountWithCode(amount, currencyCode) }}
        />
        {note ? <RowNote text={note} tag="span" /> : null}
      </RowPrice>
    ) : null}
  </Row>
);

export type BillingSummaryProps = {
  /** Loaded billing summary, or null while it's still fetching. */
  billing: Billing | null;
  /** Currency symbol to show (falls back to billing.symbol). */
  symbol: string;
  /** ISO currency code (e.g. "USD"/"MXN") shown trailing amounts to disambiguate the $ glyph. */
  currencyCode: string;
  /** Render the editable voucher control below the card (the checkout REVIEW; omitted on the
   *  read-only payment-step sidebar). */
  editableVoucher?: boolean;
};

/**
 * Sidebar billing card for the Finish step: setup fee (followed by a "Player engagement suite
 * included" line when the order carries no feature charge), product features, the
 * operating-fee note, and a Terms & Conditions link. When `editableVoucher`, an editable
 * voucher / promo-code field is shown below the card.
 */
export const BillingSummary = ({
  billing,
  symbol,
  currencyCode,
  editableVoucher,
}: BillingSummaryProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <Root>
      <PaneCard>
        <SummaryLabel text="Billing Summary" tag="paragraph" />
        <Divider />
        <BillingRow
          label={t('Setup Fee')}
          symbol={symbol}
          currencyCode={currencyCode}
          amount={billing?.setupFee ?? null}
          note={t('one time')}
        />
        {/* No feature charge (the free plan): every engagement tool comes with the setup, so say so
            on its own line under it, in place of the hidden Product Features line. */}
        {billing?.productFeatures === 0 ? <LifetimeBadge /> : null}
        {billing?.productFeatures !== 0 ? (
          <BillingRow
            label={t('Product Features')}
            sublabel={t('Cancel anytime, no extra fees')}
            sublabelSuccess
            symbol={symbol}
            currencyCode={currencyCode}
            amount={billing?.productFeatures ?? null}
            note={t('billed monthly')}
          />
        ) : null}
        {billing?.voucherCode ? (
          <BillingRow
            label={t('Discount Voucher')}
            // In the editable review the code is a removable chip (× clears it); on the read-only
            // payment-step sidebar it stays a plain pill.
            {...(editableVoucher
              ? { sublabelNode: <VoucherChip code={billing.voucherCode} /> }
              : { sublabel: billing.voucherCode, sublabelPill: true })}
            symbol={symbol}
            currencyCode={currencyCode}
            amount={billing.voucherAmount}
            discount
          />
        ) : null}
        <Divider />
        <BillingRow label={t('Platform Fee')} sublabel={t('20% of monthly revenue')} currencyCode={currencyCode} />
      </PaneCard>

      {editableVoucher ? <VoucherField inset={16} /> : null}

      <Terms href={legalUrl(i18n.language, 'terms-of-use')} target="_blank" rel="noopener noreferrer">
        <TermsLabel text="Terms & Conditions" tag="span" />
      </Terms>
    </Root>
  );
};

export type BillingFooterProps = {
  symbol: string;
  /** ISO currency code (e.g. "USD"/"MXN") shown trailing the amount to disambiguate the $ glyph. */
  currencyCode: string;
  /** Total due today (setup + first month), or null while loading. */
  total: number | null;
  /** When the order is a single one-time charge (no recurring monthly fee): label it "One time"
   *  and drop the "(setup + first month)" sub-line. */
  oneTime?: boolean;
};

/** Footer total strip pinned under the wizard navigation buttons. */
export const BillingFooter = ({ symbol, currencyCode, total, oneTime }: BillingFooterProps): React.ReactElement => (
  <FooterTotal>
    <FooterLabels>
      {oneTime ? (
        <FooterLabel text="One time" tag="span" />
      ) : (
        <>
          <FooterLabel text="Total due today" tag="span" />
          <FooterSub text="(setup + first month)" tag="span" />
        </>
      )}
    </FooterLabels>
    <FooterAmount
      text="{{symbol}}{{amount}}"
      tag="span"
      placeholders={{ symbol, amount: total == null ? EMDASH : formatAmountWithCode(total, currencyCode) }}
    />
  </FooterTotal>
);

export default BillingSummary;
